
/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useContext, useEffect, useState, useMemo } from 'react';
import { User, Session, AuthError } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { useAuthOperations } from '@/hooks/useAuthOperations';
import { cleanupAuthState } from '@/utils/authUtils';
import { migrateToSecureStorage, secureStorage } from '@/utils/secureStorage';
import { logger } from '@/utils/logger';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signUp: (email: string, password: string, metadata?: Record<string, unknown>) => Promise<{ error: AuthError | null }>;
  signIn: (email: string, password: string) => Promise<{ error: AuthError | null }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: AuthError | null }>;
  signInWithGoogle: () => Promise<{ error: AuthError | null }>;
  signInWithMagicLink: (email: string, metadata?: Record<string, unknown>) => Promise<{ error: AuthError | null }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  // Pages prerendues : loading=false pour que hydrateRoot trouve le même DOM
  // (prerender a capturé le form sans session → React doit rendre la même chose)
  const [loading, setLoading] = useState(
    document.body.dataset.prerendered !== 'true'
  );
  const { signUp, signIn, signOut, resetPassword, signInWithGoogle, signInWithMagicLink } = useAuthOperations();

  useEffect(() => {
    // Timeout de sécurité : 4 secondes max pour l'échange PKCE OAuth uniquement
    const loadingTimeout = setTimeout(() => {
      logger.warn('Timeout de chargement de session atteint, arrêt forcé');
      setLoading(false);
    }, 4000);

    // Configuration du listener d'état d'auth
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        logger.debug('🔄 Auth State Change:', event, 'Session:', !!session, 'User:', !!session?.user);
        
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);
        clearTimeout(loadingTimeout);
        
        // 🔐 SEC-003: Migration async déférée pour éviter les deadlocks
        if (event === 'SIGNED_IN' && session?.user?.id) {
          const userId = session.user.id;
          setTimeout(() => {
            migrateToSecureStorage(userId)
              .then(() => logger.debug('✅ Migration vers stockage sécurisé terminée'))
              .catch((error) => logger.error('Erreur lors de la migration vers stockage sécurisé:', error));
          }, 0);
        }
        
        // 🔐 Nettoyage du cache de clés à la déconnexion
        if (event === 'SIGNED_OUT') {
          secureStorage.clearKeyCache();
        }
      }
    );

    // Vérifier la session existante
    const checkSession = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();

        if (error) {
          logger.error('Erreur lors de la vérification de session:', error);
          cleanupAuthState();
          setLoading(false);
          clearTimeout(loadingTimeout);
        } else if (session) {
          // Session trouvée : mettre à jour l'état et arrêter le loading
          setSession(session);
          setUser(session.user);

          // 🔐 SEC-003: Migration au chargement si utilisateur connecté
          if (session.user?.id) {
            migrateToSecureStorage(session.user.id).catch((err) => {
              logger.error('Erreur migration stockage:', err);
            });
          }
          setLoading(false);
          clearTimeout(loadingTimeout);
        } else {
          // Pas de session. Si pas de callback PKCE en cours → visiteur anonyme,
          // on résout immédiatement sans attendre le timeout de 4s.
          const searchParams = new URLSearchParams(window.location.search);
          const hashParams = new URLSearchParams(window.location.hash.slice(1));
          const isPkceCallback = searchParams.has('code') || hashParams.has('access_token');
          if (!isPkceCallback) {
            setLoading(false);
            clearTimeout(loadingTimeout);
          }
          // Si PKCE callback : onAuthStateChange ou le timeout gèrent la suite
        }
      } catch (error) {
        logger.error('Erreur lors du chargement de session:', error);
        cleanupAuthState();
        setLoading(false);
        clearTimeout(loadingTimeout);
      }
    };

    checkSession();

    return () => {
      subscription.unsubscribe();
      clearTimeout(loadingTimeout);
    };
  }, []);

  const value = useMemo(() => ({
    user,
    session,
    loading,
    signUp,
    signIn,
    signOut,
    resetPassword,
    signInWithGoogle,
    signInWithMagicLink,
  }), [user, session, loading, signUp, signIn, signOut, resetPassword, signInWithGoogle, signInWithMagicLink]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
